import type { CSSProperties, RefObject } from 'react';
import { createContext, useContext } from 'react';

import type { useFloatingUI } from '../../hooks/use-floating-ui';

export interface DropdownContextValue {
  // State
  isOpen: boolean;
  activeIndex: number | null;
  size: 'sm' | 'md';

  // Actions
  setIsOpen: (open: boolean) => void;
  onItemSelect: (id: string) => void;
  registerItem: () => number;

  // Refs and floating UI
  setReference: (node: HTMLElement | null) => void;
  setFloating: (node: HTMLElement | null) => void;
  floatingStyles: CSSProperties;
  context: ReturnType<typeof useFloatingUI>['context'];
  getReferenceProps: ReturnType<typeof useFloatingUI>['getReferenceProps'];
  getFloatingProps: ReturnType<typeof useFloatingUI>['getFloatingProps'];
  getItemProps: ReturnType<typeof useFloatingUI>['getItemProps'];
  listRef: RefObject<(HTMLElement | null)[]>;
}

export const DropdownContext = createContext<DropdownContextValue | null>(null);

/**
 * Hook to access dropdown context
 * Use this in custom trigger/menu components to access dropdown state
 *
 * @example
 * ```tsx
 * function CustomTrigger() {
 *   const { isOpen, setIsOpen } = useDropdown();
 *   return (
 *     <button onClick={() => setIsOpen(!isOpen)}>
 *       {isOpen ? 'Close' : 'Open'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useDropdown() {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('useDropdown must be used within a Dropdown component');
  }
  return context;
}
